import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  CourseCSVValidationResult,
  CourseCSVUploadResult
} from '../../../functions/src/parseCourseCSV';

interface CourseCSVUploadProps {
  onUploadComplete?: (result: CourseCSVUploadResult) => void;
  onError?: (error: string) => void;
}

/**
 * 📚 Course CSV Upload Component
 * 
 * Provides a user-friendly interface for uploading course configuration CSV files.
 * Handles validation, preview, and batch upload of course configurations.
 */
export const CourseCSVUpload: React.FC<CourseCSVUploadProps> = ({
  onUploadComplete,
  onError
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState<CourseCSVValidationResult | null>(null);
  const [uploadResult, setUploadResult] = useState<CourseCSVUploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Cloud Functions
  const validateCourseCSV = httpsCallable(functions, 'validateCourseCSV');
  const parseCourseCSV = httpsCallable(functions, 'parseCourseCSV');

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback((selectedFile: File) => {
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setValidationResult(null);
      setUploadResult(null);
    } else {
      onError?.('Please select a valid CSV file');
    }
  }, [onError]);

  /**
   * Handle drag and drop
   */
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  /**
   * Read file content
   */
  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  /**
   * Validate CSV file
   */
  const handleValidate = async () => {
    if (!file) return;

    setIsValidating(true);
    setValidationResult(null);

    try {
      const csvContent = await readFileContent(file);
      const result = await validateCourseCSV({ csvContent });
      setValidationResult(result.data as CourseCSVValidationResult);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Validation failed';
      onError?.(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * Upload CSV file
   */
  const handleUpload = async () => {
    if (!file || !validationResult?.valid) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const csvContent = await readFileContent(file);
      const result = await parseCourseCSV({
        csvContent,
        fileName: file.name
      });

      const uploadData = result.data as CourseCSVUploadResult;
      setUploadResult(uploadData);
      onUploadComplete?.(uploadData);

      if (uploadData.success) {
        // Clear form on successful upload
        setFile(null);
        setValidationResult(null);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      onError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Download CSV template
   */
  const downloadTemplate = () => {
    const template = `Level,Day,Round,FingerCombination,Hand,Complexity,TrainingMode,Duration,SequenceCount,WordLengths,IncludePhrases,Quantity,SelectedWords,SelectedPhrases,RandomizeOrder
1,1,1,T_I_M,left,1,letters,120000,100,,,,,,,
1,1,2,T_I_M,left,2,letters,120000,100,,,,,,,
1,1,3,T_I_M,left,3,letters,120000,100,,,,,,,
1,1,4,T_I_M,left,4,letters,120000,100,,,,,,,
1,1,5,T_I_M,left,5,letters,120000,100,,,,,,,
1,2,1,T_I_M,right,1,letters,120000,100,,,,,,,
1,2,2,T_I_M,right,2,letters,120000,100,,,,,,,
1,2,3,T_I_M,right,3,letters,120000,100,,,,,,,
1,2,4,T_I_M,right,4,letters,120000,100,,,,,,,
1,2,5,T_I_M,right,5,letters,120000,100,,,,,,,
1,3,1,T_I_M,both,words,180000,50,"3,4,5",true,20,,,,,
1,3,2,T_I_M,both,words,180000,50,"3,4,5",true,20,,,,,
1,3,3,T_I_M,both,words,180000,50,"3,4,5",true,20,,,,,
1,3,4,T_I_M,both,words,180000,50,"3,4,5",true,20,,,,,
1,3,5,T_I_M,both,words,180000,50,"3,4,5",true,20,,,,,`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'course-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="course-csv-upload">
      <div className="upload-header">
        <h3>📚 Course CSV Upload</h3>
        <p>
          Upload course configurations in bulk using a CSV file.
          This allows you to configure multiple days and rounds at once.
        </p>
        <button
          onClick={downloadTemplate}
          className="template-button"
        >
          📥 Download Template
        </button>
      </div>

      {/* File Upload Area */}
      <div
        className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
          className="file-input"
          id="csv-file-input"
        />
        <label htmlFor="csv-file-input" className="file-label">
          <div className="upload-icon">📁</div>
          <div className="upload-text">
            {file ? file.name : 'Drop CSV file here or click to browse'}
          </div>
          <div className="upload-hint">
            Only CSV files are supported
          </div>
        </label>
      </div>

      {/* Validation Button */}
      {file && (
        <div className="validation-section">
          <button
            onClick={handleValidate}
            disabled={isValidating}
            className="validate-button"
          >
            {isValidating ? '🔍 Validating...' : '🔍 Validate CSV'}
          </button>
        </div>
      )}

      {/* Validation Results */}
      {validationResult && (
        <div className={`validation-result ${validationResult.valid ? 'valid' : 'invalid'}`}>
          <h4>Validation Results</h4>
          <div className="validation-summary">
            <span>Total Rows: {validationResult.totalRows}</span>
            <span>Valid Rows: {validationResult.validRows}</span>
            <span>Errors: {validationResult.errors.length}</span>
            <span>Warnings: {validationResult.warnings.length}</span>
          </div>

          {validationResult.errors.length > 0 && (
            <div className="validation-errors">
              <h5>Errors:</h5>
              <ul>
                {validationResult.errors.map((error, index) => (
                  <li key={index} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {validationResult.warnings.length > 0 && (
            <div className="validation-warnings">
              <h5>Warnings:</h5>
              <ul>
                {validationResult.warnings.map((warning, index) => (
                  <li key={index} className="warning-item">{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {validationResult.sampleData && validationResult.sampleData.length > 0 && (
            <div className="sample-data">
              <h5>Sample Data:</h5>
              <div className="sample-table">
                <table>
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Day</th>
                      <th>Round</th>
                      <th>FingerCombination</th>
                      <th>Hand</th>
                      <th>Complexity</th>
                      <th>TrainingMode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.sampleData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.Level || 1}</td>
                        <td>{row.Day}</td>
                        <td>{row.Round}</td>
                        <td>{row.FingerCombination}</td>
                        <td>{row.Hand}</td>
                        <td>{row.Complexity}</td>
                        <td>{row.TrainingMode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {validationResult.valid && (
            <div className="validation-actions">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="upload-button"
              >
                {isUploading ? '⬆️ Uploading...' : '⬆️ Upload Course Configurations'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <div className={`upload-result ${uploadResult.success ? 'success' : 'failed'}`}>
          <h4>Upload Results</h4>
          <div className="upload-summary">
            <span>Processed: {uploadResult.processed}</span>
            <span>Successful: {uploadResult.successful}</span>
            <span>Failed: {uploadResult.failed}</span>
            <span>Processing Time: {uploadResult.processingTime}ms</span>
          </div>

          {uploadResult.errors.length > 0 && (
            <div className="upload-errors">
              <h5>Errors:</h5>
              <ul>
                {uploadResult.errors.map((error, index) => (
                  <li key={index} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {uploadResult.warnings.length > 0 && (
            <div className="upload-warnings">
              <h5>Warnings:</h5>
              <ul>
                {uploadResult.warnings.map((warning, index) => (
                  <li key={index} className="warning-item">{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {uploadResult.success && (
            <div className="upload-success">
              <p>✅ Course configurations uploaded successfully!</p>
              <p>The configurations are now available in the training system.</p>
            </div>
          )}
        </div>
      )}

      <style>{`
        .course-csv-upload {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .upload-header {
          margin-bottom: 20px;
        }

        .upload-header h3 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .upload-header p {
          margin: 0 0 15px 0;
          color: #666;
          line-height: 1.5;
        }

        .template-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .template-button:hover {
          background: #0056b3;
        }

        .file-upload-area {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          transition: border-color 0.3s;
          margin-bottom: 20px;
        }

        .file-upload-area.drag-active {
          border-color: #007bff;
          background: #f0f8ff;
        }

        .file-input {
          display: none;
        }

        .file-label {
          cursor: pointer;
          display: block;
        }

        .upload-icon {
          font-size: 48px;
          margin-bottom: 10px;
        }

        .upload-text {
          font-size: 16px;
          font-weight: 500;
          margin-bottom: 5px;
          color: #333;
        }

        .upload-hint {
          font-size: 14px;
          color: #666;
        }

        .validation-section {
          margin-bottom: 20px;
        }

        .validate-button {
          background: #28a745;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .validate-button:hover:not(:disabled) {
          background: #218838;
        }

        .validate-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .validation-result {
          border: 1px solid;
          border-radius: 4px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .validation-result.valid {
          border-color: #28a745;
          background: #d4edda;
        }

        .validation-result.invalid {
          border-color: #dc3545;
          background: #f8d7da;
        }

        .validation-summary {
          display: flex;
          gap: 20px;
          margin-bottom: 10px;
          font-weight: 500;
        }

        .validation-errors,
        .validation-warnings {
          margin-bottom: 15px;
        }

        .validation-errors h5,
        .validation-warnings h5 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .error-item {
          color: #dc3545;
          margin-bottom: 2px;
        }

        .warning-item {
          color: #ffc107;
          margin-bottom: 2px;
        }

        .sample-data {
          margin-bottom: 15px;
        }

        .sample-data h5 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .sample-table {
          overflow-x: auto;
        }

        .sample-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .sample-table th,
        .sample-table td {
          border: 1px solid #ddd;
          padding: 4px 8px;
          text-align: left;
        }

        .sample-table th {
          background: #f8f9fa;
          font-weight: 600;
        }

        .validation-actions {
          text-align: center;
        }

        .upload-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
        }

        .upload-button:hover:not(:disabled) {
          background: #0056b3;
        }

        .upload-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .upload-result {
          border: 1px solid;
          border-radius: 4px;
          padding: 15px;
        }

        .upload-result.success {
          border-color: #28a745;
          background: #d4edda;
        }

        .upload-result.failed {
          border-color: #dc3545;
          background: #f8d7da;
        }

        .upload-summary {
          display: flex;
          gap: 20px;
          margin-bottom: 10px;
          font-weight: 500;
        }

        .upload-errors,
        .upload-warnings {
          margin-bottom: 15px;
        }

        .upload-errors h5,
        .upload-warnings h5 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .upload-success {
          text-align: center;
          color: #155724;
          font-weight: 500;
        }

        .upload-success p {
          margin: 5px 0;
        }
      `}</style>
    </div>
  );
};

export default CourseCSVUpload;
